import * as Sentry from "@sentry/react";

/**
 * Available log levels.
 */
enum LogLevel {
    INFO = 'info',
    WARN = 'warn',
    ERROR = 'error',
}

/**
 * Context for logging, can include any additional information relevant to the log entry.
 */
type LogContext = Record<string, unknown>;

/**
 * Converts an unknown value to an Error object.
 * @param {unknown} unknown The unknown value to convert.
 * @returns {Error} The resulting Error object.
 */
function unknownToError(unknown: unknown): Error {
    if (unknown instanceof Error) return unknown;
    return new Error(typeof unknown === 'string' ? unknown : 'Unknown error');
}

/**
 * Logs a message.
 * @param {LogLevel} level Log level.
 * @param {string} message Message.
 * @param {LogContext} context Optional context.
 */
function logMessage(level: LogLevel, message: string, context: LogContext = {}): void {
    const time: string = new Date().toISOString();

    const logEntry: string = `${time} [${level.toUpperCase()}] ${message}`;

    switch (level) {
        case LogLevel.INFO:
            console.info(logEntry, context);
            break;
        case LogLevel.WARN:
            console.warn(logEntry, context);
            Sentry.captureMessage(message, {
                level: 'warning',
                extra: context
            });
            break;
        case LogLevel.ERROR: {
            console.error(logEntry, context);
            const error = unknownToError(context.error ?? message);
            Sentry.captureException(error, {
                level: 'error',
                extra: context
            });
            break;
        }
        default:
            console.log(logEntry, context);
    }
}

class Logger {
    /**
     * Logs info message.
     * @param {string} message Log message.
     * @param {LogContext} context Optional context.
     * @returns {void}
     */
    info(message: string, context?: LogContext): void {
        return logMessage(LogLevel.INFO, message, context);
    }

    /**
     * Logs warning message.
     * @param {string} message Log message.
     * @param {LogContext} context Optional context.
     * @returns {void}
     */
    warn(message: string, context?: LogContext): void {
        return logMessage(LogLevel.WARN, message, context);
    }

    /**
     * Logs error message.
     * @param {string} message Log message.
     * @param {LogContext} context Optional context.
     * @returns {void}
     */
    error(message: string, context?: LogContext): void {
        return logMessage(LogLevel.ERROR, message, context);
    }

    /**
     * Logs unknown error.
     * @param {unknown} error The error to log.
     * @param {LogContext} context Optional context.
     * @returns {void}
     */
    unknownError(error: unknown, context?: LogContext): void {
        // unknownToError here makes sure we have actual typed Error object instead of unknown value.
        const err = unknownToError(error);
        return logMessage(LogLevel.ERROR, err.message, { ...context, error: err });
    }
}

export const logger = new Logger();
